import type { ShiptimeCarrierSelectionDecisionDetails, ShiptimeShipmentResponse } from '@shipium-corp/shipium-ts-types';
import type { ActiveRuleSummary, RuleProcessingStatus } from './types';

export interface TimelineEntry {
  rule: ActiveRuleSummary;
  status: RuleProcessingStatus;
  /** Service method IDs newly present in the pool after this rule applied. */
  added: string[];
  /** Service method IDs that disappeared from the pool after this rule applied. */
  removed: string[];
  /** Resulting pool snapshot after the rule executed; null for non-applied rules. */
  appliedAfter: string[] | null;
}

export interface ProcessedTimeline {
  /** Pool of service method IDs entering rule processing (post-carrier-filter). */
  initialPool: string[];
  /** Authoritative post-everything pool (read straight from `serviceMethodsAfterFiltering`). */
  finalPool: string[];
  /** ID of the carrier-selected service method when the API returned status === 'success'. */
  selectedMethodId?: string;
  /** Every active (or opted-in inactive) rule in evaluation order with derived status. */
  timeline: TimelineEntry[];
  /** Timeline slice up to and including the first applied terminating rule. */
  beforeTimeline: TimelineEntry[];
  /** First applied terminating rule, if any — used for the "stopped processing" label. */
  terminatingRule?: ActiveRuleSummary;
  /**
   * Post-terminator entries that get a full timeline row: any "would have matched" rule plus
   * any opted-in inactive rule that didn't match.
   */
  postTerminatorItems: TimelineEntry[];
}

const isRuleFilterReason = (reason: string): boolean => /^Rule selection by/i.test(reason);

// Resolves the pool entering rule processing from the response.
function derivePoolBoundaries(decisionDetails: ShiptimeCarrierSelectionDecisionDetails | undefined): {
  initialPool: string[];
  finalPool: string[];
} {
  const availableMethods = decisionDetails?.availableServiceMethods ?? [];

  // `filteredServiceMethods` references the `serviceMethodIdentifier`; translate back to the
  // plain `carrierServiceMethodId` so the result lines up with the other arrays.
  const idByIdentifier = new Map(
    availableMethods
      .filter((m) => m.serviceMethodIdentifier && m.carrierServiceMethodId)
      .map((m) => [m.serviceMethodIdentifier as string, m.carrierServiceMethodId as string]),
  );

  const finalPool = (decisionDetails?.serviceMethodsAfterFiltering ?? [])
    .map((m) => m.carrierServiceMethodId)
    .filter((id): id is string => Boolean(id));

  // Methods a rule removed and not restored back. Methods that a rule removed but a later
  // RESTORE brought back show up in the final pool, so that check excludes them.
  const ruleRemoved: string[] = [];
  for (const f of decisionDetails?.filteredServiceMethods ?? []) {
    if (!isRuleFilterReason(f.filterReason ?? '')) continue;
    const methodId = f.serviceMethodIdentifier ? idByIdentifier.get(f.serviceMethodIdentifier) : undefined;
    if (!methodId) continue;
    if (finalPool.includes(methodId)) continue;
    if (ruleRemoved.includes(methodId)) continue;
    ruleRemoved.push(methodId);
  }

  return { initialPool: [...finalPool, ...ruleRemoved], finalPool };
}

/** Derives the rule timeline + pool snapshots the Results view renders. */
export function buildProcessedTimeline(
  response: ShiptimeShipmentResponse,
  activeRulesInOrder: ActiveRuleSummary[],
): ProcessedTimeline {
  const carrierSelection = response.carrierSelection;
  const carrierDecision = response.details?.carrierSelection?.carrierDecision;
  // The full processing result (on details) carries rulesThatWouldHaveApplied and rulesNotApplied;
  // the trimmed summary only has rulesApplied. Rules in rulesNotApplied — and any rule the engine
  // never reached — fall through to the 'skipped' status below.
  const ruleResult = carrierDecision?.details?.ruleSetProcessingResult;
  const rulesApplied = ruleResult?.rulesApplied ?? [];
  const rulesThatWouldHaveApplied = ruleResult?.rulesThatWouldHaveApplied ?? [];

  const { initialPool, finalPool } = derivePoolBoundaries(carrierDecision?.details);

  const appliedById = new Map(rulesApplied.map((r) => [r.ruleId as string, r]));
  const wouldHaveById = new Map(rulesThatWouldHaveApplied.map((r) => [r.ruleId as string, r]));

  // Thread `runningPool` through applied rules to derive per-step deltas; skipped + would-have
  // rules don't touch the pool.
  const timeline: TimelineEntry[] = [];
  let runningPool: string[] = initialPool;
  for (const rule of activeRulesInOrder) {
    let status: RuleProcessingStatus = 'skipped';
    let added: string[] = [];
    let removed: string[] = [];
    let appliedAfter: string[] | null = null;

    const appliedEntry = appliedById.get(rule.ruleId);
    if (appliedEntry) {
      status = 'applied';
      const after = appliedEntry.serviceMethodIdsAfterExecutingRule ?? [];
      added = after.filter((m) => !runningPool.includes(m));
      removed = runningPool.filter((m) => !after.includes(m));
      appliedAfter = after;
      runningPool = after;
    } else if (wouldHaveById.has(rule.ruleId)) {
      status = 'wouldHaveApplied';
    }

    timeline.push({ rule, status, added, removed, appliedAfter });
  }

  // Split at the first applied terminating rule
  const terminatingIdx = timeline.findIndex((t) => t.status === 'applied' && t.rule.terminating);
  const hasTerminator = terminatingIdx >= 0;
  const beforeTimeline = hasTerminator ? timeline.slice(0, terminatingIdx + 1) : timeline;
  const afterRows = hasTerminator ? timeline.slice(terminatingIdx + 1) : [];
  const terminatingRule = hasTerminator ? beforeTimeline[beforeTimeline.length - 1].rule : undefined;

  // Every rule after the terminator, in evaluation order. Each carries its own
  // status tag (Would Have Applied / Does Not Apply); the section header conveys
  // that none of them were actually evaluated.
  const postTerminatorItems = afterRows;

  return {
    initialPool,
    finalPool,
    selectedMethodId: carrierSelection?.carrierServiceMethodId,
    timeline,
    beforeTimeline,
    terminatingRule,
    postTerminatorItems,
  };
}

export interface ResolvedMethod {
  id: string;
  name: string;
}

export interface ResolvedTimelineEntry {
  rule: ActiveRuleSummary;
  status: RuleProcessingStatus;
  added: ResolvedMethod[];
  removed: ResolvedMethod[];
  appliedAfter: ResolvedMethod[] | null;
}

export const resolveTimelineEntry = (
  entry: TimelineEntry,
  methodNameById: Record<string, string>,
): ResolvedTimelineEntry => {
  const resolve = (id: string): ResolvedMethod => ({ id, name: methodNameById[id] ?? id });
  return {
    rule: entry.rule,
    status: entry.status,
    added: entry.added.map(resolve),
    removed: entry.removed.map(resolve),
    appliedAfter: entry.appliedAfter ? entry.appliedAfter.map(resolve) : null,
  };
};
